import { UserType } from "@/constants";

export const GOVERNMENT_PREFIX = "/government" as const;
export const OPERATOR_PREFIX = "/operator" as const;

export type PortalPrefix = typeof GOVERNMENT_PREFIX | typeof OPERATOR_PREFIX;

export interface PortalNavItem {
  to: string;
  label: string;
  /** Pathname prefix for active styling (e.g. /government/registrations) */
  pathPrefix: string;
}

export interface PortalNavOptions {
  includeForeclosureCases?: boolean;
  includeResidentialOwners?: boolean;
}

export function getPortalNavItems(
  userType: UserType,
  options?: PortalNavOptions,
): PortalNavItem[] {
  if (userType === UserType.GOVERNMENT) {
    const items: PortalNavItem[] = [
      {
        to: "/government/overview",
        label: "Dashboard",
        pathPrefix: "/government/overview",
      },
      {
        to: "/government/ordinances",
        label: "Ordinances",
        pathPrefix: "/government/ordinances",
      },
      {
        to: "/government/regions",
        label: "Regions",
        pathPrefix: "/government/regions",
      },
      {
        to: "/government/registrations",
        label: "Licenses",
        pathPrefix: "/government/registrations",
      },
    ];
    if (options?.includeForeclosureCases) {
      items.push({
        to: "/government/cases",
        label: "Cases",
        pathPrefix: "/government/cases",
      });
    }
    items.push(
      {
        to: "/government/short-term-rentals",
        label: "Short-Term Rentals",
        pathPrefix: "/government/short-term-rentals",
      },
      {
        to: "/government/unregistered-properties",
        label: "Unregistered Properties",
        pathPrefix: "/government/unregistered-properties",
      },
      {
        to: "/government/inspections",
        label: "Inspections",
        pathPrefix: "/government/inspections",
      },
      {
        to: "/government/reports",
        label: "Reports",
        pathPrefix: "/government/reports",
      },
      {
        to: "/government/setup",
        label: "Users",
        pathPrefix: "/government/setup",
      },
    );
    return items;
  }
  if (userType === UserType.OPERATOR) {
    const items: PortalNavItem[] = [
      {
        to: "/operator/overview",
        label: "Dashboard",
        pathPrefix: "/operator/overview",
      },
      {
        to: "/operator/jurisdictions",
        label: "Jurisdictions",
        pathPrefix: "/operator/jurisdictions",
      },
      {
        to: "/operator/ordinances",
        label: "Ordinances",
        pathPrefix: "/operator/ordinances",
      },
      {
        to: "/operator/regions",
        label: "Regions",
        pathPrefix: "/operator/regions",
      },
      {
        to: "/operator/registrations",
        label: "Licenses",
        pathPrefix: "/operator/registrations",
      },
    ];
    items.push({
      to: "/operator/cases",
      label: "Cases",
      pathPrefix: "/operator/cases",
    });
    items.push({
      to: "/operator/str/listings",
      label: "STR",
      pathPrefix: "/operator/str",
    });
    items.push({
      to: "/operator/title-requests",
      label: "Title Requests",
      pathPrefix: "/operator/title-requests",
    });
    items.push({
      to: "/operator/unregistered-properties",
      label: "Unregistered Properties",
      pathPrefix: "/operator/unregistered-properties",
    });
    if (options?.includeResidentialOwners) {
      items.push({
        to: "/operator/residential-property-owners",
        label: "Residential Property Owners",
        pathPrefix: "/operator/residential-property-owners",
      });
    }
    items.push(
      {
        to: "/operator/inspections",
        label: "Inspections",
        pathPrefix: "/operator/inspections",
      },
      {
        to: "/operator/checks",
        label: "Financials",
        pathPrefix: "/operator/financials",
      },
      {
        to: "/operator/reports",
        label: "Reports",
        pathPrefix: "/operator/reports",
      },
    );
    return items;
  }
  return [];
}

const LEGACY_GOV_TAB_TO_PATH: Readonly<Record<string, string>> = {
  jurisdictions: "/government/overview",
  ordinances: "/government/ordinances",
  regions: "/government/regions",
  registrations: "/government/registrations",
  cases: "/government/cases",
  "short-term-rentals": "/government/short-term-rentals",
  "inactive-registrations": "/government/inactive-registrations",
  "unregistered-properties": "/government/unregistered-properties",
  inspections: "/government/inspections",
  reports: "/government/reports",
  users: "/government/setup",
};

const LEGACY_OP_TAB_TO_PATH: Readonly<Record<string, string>> = {
  jurisdictions: "/operator/jurisdictions",
  "attom-tax-assessor-status": "/operator/setup",
  ordinances: "/operator/ordinances",
  regions: "/operator/regions",
  registrations: "/operator/registrations",
  cases: "/operator/cases",
  "inactive-registrations": "/operator/inactive-registrations",
  "unregistered-properties": "/operator/unregistered-properties",
  inspections: "/operator/inspections",
  checks: "/operator/checks",
  invoices: "/operator/invoices",
  refunds: "/operator/refunds",
  reports: "/operator/reports",
  forecasting: "/operator/forecasting",
  "system-usage": "/operator/system-usage",
};

/**
 * Resolves a legacy /dashboard?tab= value to the new work route.
 */
export function legacyTabToPath(
  portal: "government" | "operator",
  tab: string | undefined,
): string {
  if (portal === "government") {
    if (tab && LEGACY_GOV_TAB_TO_PATH[tab]) {
      return LEGACY_GOV_TAB_TO_PATH[tab];
    }
    return "/government/overview";
  }
  if (tab && LEGACY_OP_TAB_TO_PATH[tab]) {
    return LEGACY_OP_TAB_TO_PATH[tab];
  }
  return "/operator/overview";
}

const PORTAL_HOME_LABEL: Record<PortalPrefix, string> = {
  "/government": "Government",
  "/operator": "Operator",
};

const PATH_SEGMENT_TO_CRUMB: Record<string, string> = {
  overview: "Overview",
  jurisdictions: "Jurisdictions",
  "attom-tax-assessor-status": "ATTOM Status",
  ordinances: "Ordinances",
  regions: "Regions",
  registrations: "Licenses",
  cases: "Cases",
  "short-term-rentals": "Short-Term Rentals",
  str: "STR",
  "title-requests": "Title Requests",
  deduplication: "Deduplication",
  "inactive-registrations": "Inactive Licenses",
  "unregistered-properties": "Unregistered Properties",
  "residential-property-owners": "Residential Property Owners",
  inspections: "Inspections",
  checks: "Checks",
  invoices: "Invoices",
  refunds: "Refunds",
  reports: "Reports",
  forecasting: "Forecasting",
  "system-usage": "System Usage",
  quickbooks: "QuickBooks",
};

export function getPortalBreadcrumbItems(
  portal: PortalPrefix,
  pathname: string,
): { label: string; to?: string }[] {
  const homeLabel = PORTAL_HOME_LABEL[portal];
  const crumbs: { label: string; to?: string }[] = [
    { label: homeLabel, to: `${portal}/overview` },
  ];
  if (pathname === `${portal}/overview` || pathname === portal) {
    return crumbs;
  }
  const rest = pathname.replace(`${portal}/`, "");
  const first = rest.split("/")[0] ?? "";
  if (first && first !== "overview" && PATH_SEGMENT_TO_CRUMB[first]) {
    crumbs.push({ label: PATH_SEGMENT_TO_CRUMB[first] });
  }
  return crumbs;
}

/**
 * H1 for portal work pages: same wording as the sub-nav label for that section
 * (see [getPortalNavItems]).
 */
export function getPortalPageTitleForSection(pathSegment: string): string {
  return PATH_SEGMENT_TO_CRUMB[pathSegment] ?? pathSegment;
}

const PORTAL_SETUP_PATHS = new Set([
  "/operator/setup",
  "/government/setup",
  "/operator/question-configuration",
  "/government/question-configuration",
]);

/** True on setup / question-configuration pages where main portal sub-nav is hidden. */
export function isPortalSetupPath(pathname: string): boolean {
  return PORTAL_SETUP_PATHS.has(pathname);
}

const OPERATOR_DASHBOARD_PREFIXES = [
  "/operator/overview",
  "/operator/forecasting",
  "/operator/system-usage",
  "/operator/quickbooks",
] as const;

/** True on pages reached from the operator dashboard sub-tabs. */
export function isOperatorDashboardPath(pathname: string): boolean {
  return OPERATOR_DASHBOARD_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

const OPERATOR_FINANCIAL_PREFIXES = [
  "/operator/checks",
  "/operator/invoices",
  "/operator/refunds",
] as const;

/** True on Checks, Invoices, and Refunds, which share the Financials tab. */
export function isOperatorFinancialsPath(pathname: string): boolean {
  return OPERATOR_FINANCIAL_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/** True on the STR section, including later subtabs under /operator/str. */
export function isOperatorStrPath(pathname: string): boolean {
  return pathname === "/operator/str" || pathname.startsWith("/operator/str/");
}

export function isPortalNavItemActive(
  pathname: string,
  item: PortalNavItem,
): boolean {
  if (item.pathPrefix === "/operator/overview") {
    return isOperatorDashboardPath(pathname);
  }
  if (item.pathPrefix === "/operator/financials") {
    return isOperatorFinancialsPath(pathname);
  }
  if (pathname === item.pathPrefix) return true;
  return pathname.startsWith(`${item.pathPrefix}/`);
}

export function getOperatorFinancialsSubNavItems(): PortalNavItem[] {
  return [
    {
      to: "/operator/checks",
      label: "Checks",
      pathPrefix: "/operator/checks",
    },
    {
      to: "/operator/invoices",
      label: "Invoices",
      pathPrefix: "/operator/invoices",
    },
    {
      to: "/operator/refunds",
      label: "Refunds",
      pathPrefix: "/operator/refunds",
    },
  ];
}

export function getOperatorStrSubNavItems(): PortalNavItem[] {
  return [
    {
      to: "/operator/str/listings",
      label: "Listings",
      pathPrefix: "/operator/str/listings",
    },
    {
      to: "/operator/str/properties",
      label: "Properties",
      pathPrefix: "/operator/str/properties",
    },
    {
      to: "/operator/str/address-mapping",
      label: "Address mapping",
      pathPrefix: "/operator/str/address-mapping",
    },
    {
      to: "/operator/str/deduplication",
      label: "Deduplication",
      pathPrefix: "/operator/str/deduplication",
    },
  ];
}

export function getOperatorDashboardSubNavItems(): PortalNavItem[] {
  return [
    {
      to: "/operator/overview",
      label: "V0",
      pathPrefix: "/operator/overview",
    },
    {
      to: "/operator/overview/leadership",
      label: "Leadership",
      pathPrefix: "/operator/overview/leadership",
    },
    {
      to: "/operator/overview/compliance",
      label: "Compliance",
      pathPrefix: "/operator/overview/compliance",
    },
    {
      to: "/operator/overview/hubspot",
      label: "HubSpot",
      pathPrefix: "/operator/overview/hubspot",
    },
    {
      to: "/operator/overview/sales-commission",
      label: "Sales Commission Tracker",
      pathPrefix: "/operator/overview/sales-commission",
    },
    {
      to: "/operator/forecasting",
      label: "Forecasting",
      pathPrefix: "/operator/forecasting",
    },
    {
      to: "/operator/system-usage",
      label: "System Usage",
      pathPrefix: "/operator/system-usage",
    },
    {
      to: "/operator/quickbooks",
      label: "QuickBooks",
      pathPrefix: "/operator/quickbooks",
    },
  ];
}
